import { RefreshCw, Unplug } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNodeReachability, UNREACHABLE_INTERVAL_MS } from "../../hooks/useNodeReachability";
import { reloadPage, UI_RELOAD_DELAY_MS } from "../../lib/uiBuild";
import { isRemoteSession, useAccessSession } from "../../stores/accessSession";
import { Button } from "../common/Button";
import styles from "./NodeUnreachableBanner.module.css";

/**
 * The node that serves this UI stopped answering (remote-node program N3
 * half 2, 2026-09-08). Mounted once in the Shell; renders nothing while the
 * node answers. When it does not: what happened, for THIS host, what to do,
 * and a button that tries now (Rule 26 / Rule 29 — a client left with a page
 * that silently fails is a dead end). Wording follows where the UI came
 * from: a remote node may be asleep or off; the local server may have been
 * stopped.
 */
export function NodeUnreachableBanner() {
	const { t } = useTranslation();
	const r = useNodeReachability();
	const session = useAccessSession();
	useEffect(() => {
		// The server is already serving a newer build (2026-10-21). Reload
		// this tab onto it. The button below is the same reload, immediately.
		if (!r.reachable || !r.staleBuild) return undefined;
		const id = window.setTimeout(() => reloadPage(), UI_RELOAD_DELAY_MS);
		return () => window.clearTimeout(id);
	}, [r.reachable, r.staleBuild]);
	if (r.reachable && r.staleBuild) {
		return (
			<div className={styles.banner} role="status" aria-live="polite" data-ui-updated>
				<RefreshCw size={16} aria-hidden className={styles.icon} />
				<div className={styles.body}>
					<div className={styles.title}>{t("nav.uiUpdatedTitle", "AgntSpace was updated")}</div>
					<div className={styles.cause}>{t("nav.uiUpdatedCause", "This page is reloading so you get the version the server is running.")}</div>
				</div>
				<Button size="sm" variant="primary" onClick={() => reloadPage()}>{t("nav.uiUpdatedReload", "Reload now")}</Button>
			</div>
		);
	}
	if (r.reachable) return null;
	const remote = isRemoteSession(session);
	const host = typeof window === "undefined" ? "" : window.location.host;
	const downFor = r.downSince ? Math.max(0, Math.round((Date.now() - r.downSince) / 1000)) : 0;
	return (
		<div className={styles.banner} role="status" aria-live="polite" data-node-unreachable>
			<Unplug size={16} aria-hidden className={styles.icon} />
			<div className={styles.body}>
				<div className={styles.title}>
					{remote
						? t("nav.nodeUnreachableRemote", "Can't reach {{host}}", { host })
						: t("nav.nodeUnreachableLocal", "AgntSpace on this computer stopped answering")}
				</div>
				<div className={styles.cause}>
					{t("nav.nodeUnreachableCause", "{{detail}} for {{seconds}} s — trying again every {{every}} s.", { detail: r.detail || "no answer", seconds: downFor, every: Math.round(UNREACHABLE_INTERVAL_MS / 1000) })}
				</div>
				<ul className={styles.options}>
					{remote ? (
						<>
							<li>{t("nav.nodeUnreachableOptRemote1", "It may be asleep, off, or on a network this device cannot reach right now — wake it, or move to its network.")}</li>
							<li>{t("nav.nodeUnreachableOptRemote2", "Nothing you did here is lost on that machine; whatever you send while it is away is not sent. In the desktop app, the tray menu offers “Use this computer instead”.")}</li>
						</>
					) : (
						<li>{t("nav.nodeUnreachableOptLocal", "Start it again — from a terminal: agntspace start — then this page reconnects by itself.")}</li>
					)}
				</ul>
			</div>
			<Button size="sm" variant="secondary" loading={r.probing} onClick={() => void r.probe()}>
				{t("nav.nodeUnreachableRetry", "Try now")}
			</Button>
		</div>
	);
}
